import { handleWorkerOutMessage } from './client-state.js';
import type { SymbolIndexWorkerInMessage, SymbolIndexWorkerOutMessage } from './protocol.js';

class InProcessWorkerBridge {
  private readonly listeners = new Set<(message: SymbolIndexWorkerOutMessage) => void>();
  private dispatch: ((message: SymbolIndexWorkerInMessage) => void) | null = null;
  private readonly readyPromise: Promise<void>;

  constructor(attachHost: typeof import('./host.js').attachSymbolIndexWorkerHost) {
    this.readyPromise = Promise.resolve().then(() => {
      attachHost({
        postMessage: (message) => {
          queueMicrotask(() => {
            for (const listener of this.listeners) listener(message);
          });
        },
        onMessage: (handler) => {
          this.dispatch = handler;
        },
      });
    });
  }

  async waitReady(): Promise<void> {
    await this.readyPromise;
  }

  postMessage(message: SymbolIndexWorkerInMessage): void {
    queueMicrotask(() => {
      this.dispatch?.(message);
    });
  }

  on(event: 'message', listener: (message: SymbolIndexWorkerOutMessage) => void): void {
    if (event !== 'message') return;
    this.listeners.add(listener);
  }

  off(event: 'message', listener: (message: SymbolIndexWorkerOutMessage) => void): void {
    if (event !== 'message') return;
    this.listeners.delete(listener);
  }

  terminate(): void {
    this.listeners.clear();
    this.dispatch = null;
  }
}

let inProcessBridge: InProcessWorkerBridge | null = null;
let inProcessBridgeReady: Promise<void> | null = null;

export function getInProcessBridge(): InProcessWorkerBridge | null {
  return inProcessBridge;
}

export function clearInProcessBridge(): void {
  if (inProcessBridge != null) {
    inProcessBridge.terminate();
    inProcessBridge = null;
  }
  inProcessBridgeReady = null;
}

export async function ensureInProcessBridge(): Promise<InProcessWorkerBridge> {
  if (inProcessBridge != null) return inProcessBridge;
  const { attachSymbolIndexWorkerHost } = await import('./host.js');
  inProcessBridge = new InProcessWorkerBridge(attachSymbolIndexWorkerHost);
  inProcessBridge.on('message', handleWorkerOutMessage);
  inProcessBridgeReady = inProcessBridge.waitReady();
  await inProcessBridgeReady;
  return inProcessBridge;
}
